/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.SYSTEM.BULK_IMPORT — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/BulkImportPage.tsx. That desktop page
 * hosts the full CSV/XLSX <ImportEngine> wizard — target selection, file upload,
 * column mapping, per-row commit through the Common Routine. All of that is a
 * desktop-heavy authoring flow and stays on the desktop (§16 — never the wizard
 * re-hosted in a phone viewport).
 *
 * On mobile this becomes the READ-ONLY provenance monitor: a scannable list of
 * recent import/export runs from the UPP run log (upp_run — the platform's
 * import/export provenance table, populated by the same engine the desktop wizard
 * drives), with KPI tiles and a per-run detail sheet showing status + row/error
 * counts + who ran it + when. Presentation only differs; the data/logic layer is
 * shared:
 *   - reads via apiDb (upp_run, plus upp_definition + csm_user_master roll-ups to
 *     resolve the definition name and the operator — §17.B4, never a raw id),
 *   - it performs NO writes, so routed_writes is not imported (§2 is moot here —
 *     there is nothing to mutate on this screen).
 *
 * direction / format / status are CHECK-enum / code literals (§17.B11) — their
 * labels go through t() labelled constants, never the bare stored code. Dates use
 * useFormatDateTime (§11 — never a raw ISO string).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, FileDown, FileUp, X, CircleCheck, CircleAlert, CircleX } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobileLeadTone, type MobilePillTone,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

type Direction = 'IMPORT' | 'EXPORT';
type RunStatus = 'OK' | 'PARTIAL' | 'FAILED';

interface MobileRunRow {
  upp_run_id: number;
  definition_name: string;
  entity_name: string | null;
  direction: string | null;
  format: string | null;
  row_count: number;
  error_count: number;
  status: string;
  ran_by_name: string;
  created_at: string;
}

type DirFacet = 'all' | Direction;
type StatusFacet = 'all' | RunStatus;

// §17.B11: CHECK-enum / code labels defined once as labelled constants, never
// bare literals scattered through the JSX. The stored value stays the code.
interface EnumOption { value: string; labelKey: string; fallback: string }

const DIRECTION_OPTIONS: EnumOption[] = [
  { value: 'IMPORT', labelKey: 'mobile.import.direction.IMPORT', fallback: 'Import' },
  { value: 'EXPORT', labelKey: 'mobile.import.direction.EXPORT', fallback: 'Export' },
];
const FORMAT_OPTIONS: EnumOption[] = [
  { value: 'CSV', labelKey: 'mobile.import.format.CSV', fallback: 'CSV' },
  { value: 'XLSX', labelKey: 'mobile.import.format.XLSX', fallback: 'XLSX' },
  { value: 'PDF', labelKey: 'mobile.import.format.PDF', fallback: 'PDF' },
  { value: 'JSON', labelKey: 'mobile.import.format.JSON', fallback: 'JSON' },
];
const STATUS_OPTIONS: EnumOption[] = [
  { value: 'OK', labelKey: 'mobile.import.status.OK', fallback: 'Completed' },
  { value: 'PARTIAL', labelKey: 'mobile.import.status.PARTIAL', fallback: 'Partial' },
  { value: 'FAILED', labelKey: 'mobile.import.status.FAILED', fallback: 'Failed' },
];

// Status → pill tone (§17.B10 style badges): OK green, PARTIAL amber, FAILED red.
function statusTone(status: string): MobilePillTone {
  if (status === 'OK') return 'ok';
  if (status === 'PARTIAL') return 'wait';
  return 'no';
}

// A recent window keeps the list scannable on a phone; the desktop keeps the full log.
const RECENT_LIMIT = 200;

export function MobileBulkImportPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [runs, setRuns] = useState<MobileRunRow[]>([]);
  const [filter, setFilter] = useState('');
  const [dirFacet, setDirFacet] = useState<DirFacet>('all');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [selected, setSelected] = useState<MobileRunRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve a CHECK-enum / code to its translated label (§17.B11).
  const enumLabel = (options: EnumOption[], value: string | null): string => {
    if (!value) return t('common.none', 'None');
    const opt = options.find((o) => o.value === value);
    return opt ? t(opt.labelKey, opt.fallback) : value;
  };

  const reload = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data: rawRuns, error: rErr } = await apiDb
        .from('upp_run')
        .select('upp_run_id, upp_definition_id, direction, format, entity_name, row_count, error_count, status, ran_by_user_id, created_at')
        .order('created_at', { ascending: false })
        .limit(RECENT_LIMIT);
      if (rErr) throw rErr;

      type RawRun = {
        upp_run_id: number;
        upp_definition_id: number | null;
        direction: string | null;
        format: string | null;
        entity_name: string | null;
        row_count: number;
        error_count: number;
        status: string;
        ran_by_user_id: number | null;
        created_at: string;
      };
      const list = (rawRuns ?? []) as RawRun[];

      // Resolve definition ids → names (§17.B4). upp_run.upp_definition_id is
      // nullable (ON DELETE SET NULL), so an orphaned run falls back to its entity.
      const defIds = [...new Set(list.map((r) => r.upp_definition_id).filter((id): id is number => id != null))];
      const defNames: Record<number, string> = {};
      if (defIds.length > 0) {
        const { data: rawDefs, error: dErr } = await apiDb
          .from('upp_definition')
          .select('upp_definition_id, name, internal_code')
          .in('upp_definition_id', defIds);
        if (dErr) throw dErr;
        for (const d of (rawDefs ?? []) as { upp_definition_id: number; name: string; internal_code: string }[]) {
          defNames[d.upp_definition_id] = d.name || d.internal_code;
        }
      }

      // Resolve operator ids → display names (§17.B4). upp_run has no FK on
      // ran_by_user_id, so we resolve it with a separate map rather than an embed.
      const userIds = [...new Set(list.map((r) => r.ran_by_user_id).filter((id): id is number => id != null))];
      const userNames: Record<number, string> = {};
      if (userIds.length > 0) {
        const { data: rawUsers, error: uErr } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', userIds);
        if (uErr) throw uErr;
        for (const u of (rawUsers ?? []) as { user_id: number; display_name: string | null; email: string | null }[]) {
          userNames[u.user_id] = u.display_name || u.email || String(u.user_id);
        }
      }

      const mapped: MobileRunRow[] = list.map((r) => ({
        upp_run_id: r.upp_run_id,
        definition_name:
          (r.upp_definition_id != null ? defNames[r.upp_definition_id] : undefined) ??
          r.entity_name ??
          t('mobile.import.ad_hoc', 'Ad-hoc run'),
        entity_name: r.entity_name,
        direction: r.direction,
        format: r.format,
        row_count: r.row_count,
        error_count: r.error_count,
        status: r.status,
        ran_by_name: r.ran_by_user_id != null ? (userNames[r.ran_by_user_id] ?? t('common.system', 'System')) : t('common.system', 'System'),
        created_at: r.created_at,
      }));
      setRuns(mapped);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
    // `t` is deliberately NOT a dependency: the FETCH does not depend on the
    // language — only the fallback labels baked into the rows do. Depending on it
    // would refetch the whole list on every language change, and t's identity is
    // not contractually stable (react-i18next may return a new function per
    // render), which turns this into a render→fetch→render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return runs.filter((r) => {
      if (dirFacet !== 'all' && r.direction !== dirFacet) return false;
      if (statusFacet !== 'all' && r.status !== statusFacet) return false;
      if (!q) return true;
      return (
        r.definition_name.toLowerCase().includes(q) ||
        (r.entity_name ?? '').toLowerCase().includes(q) ||
        r.ran_by_name.toLowerCase().includes(q)
      );
    });
  }, [runs, filter, dirFacet, statusFacet]);

  // KPI roll-up over the loaded window (best-effort headline metrics).
  const kpis = useMemo(() => {
    const totalRows = runs.reduce((sum, r) => sum + (r.row_count ?? 0), 0);
    const totalErrors = runs.reduce((sum, r) => sum + (r.error_count ?? 0), 0);
    const failed = runs.filter((r) => r.status === 'FAILED').length;
    return { runs: runs.length, totalRows, totalErrors, failed };
  }, [runs]);

  const dirFacets: { key: DirFacet; label: string }[] = [
    { key: 'all', label: t('mobile.import.all', 'All') },
    { key: 'IMPORT', label: t('mobile.import.direction.IMPORT', 'Import') },
    { key: 'EXPORT', label: t('mobile.import.direction.EXPORT', 'Export') },
  ];
  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.import.all', 'All') },
    { key: 'OK', label: t('mobile.import.status.OK', 'Completed') },
    { key: 'PARTIAL', label: t('mobile.import.status.PARTIAL', 'Partial') },
    { key: 'FAILED', label: t('mobile.import.status.FAILED', 'Failed') },
  ];

  const rowsLabel = (n: number) => t('mobile.import.rows_count', { count: n, defaultValue: `${n} rows` });
  const errorsLabel = (n: number) => t('mobile.import.errors_count', { count: n, defaultValue: `${n} errors` });

  const leadFor = (r: MobileRunRow): { tone: MobileLeadTone; icon: JSX.Element } => {
    if (r.direction === 'EXPORT') return { tone: 'slate', icon: <FileDown /> };
    if (r.status === 'FAILED') return { tone: 'red', icon: <FileUp /> };
    if (r.status === 'PARTIAL') return { tone: 'amber', icon: <FileUp /> };
    return { tone: 'green', icon: <FileUp /> };
  };

  const statusIcon = (status: string) => {
    if (status === 'OK') return <CircleCheck size={20} />;
    if (status === 'PARTIAL') return <CircleAlert size={20} />;
    return <CircleX size={20} />;
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.import.title', 'Import & Export Runs')}
        description={t('mobile.import.description', 'Recent bulk import and export activity, with row and error counts.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.runs.toLocaleString(), label: t('mobile.import.kpi.runs', 'Runs') },
          { value: kpis.totalRows.toLocaleString(), label: t('mobile.import.kpi.rows', 'Rows') },
          { value: kpis.totalErrors.toLocaleString(), label: t('mobile.import.kpi.errors', 'Errors') },
          { value: kpis.failed.toLocaleString(), label: t('mobile.import.kpi.failed', 'Failed') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.import.search', 'Search runs')}
          aria-label={t('mobile.import.search', 'Search runs')}
        />
      </div>

      <MobileFilterChips
        chips={dirFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => dirFacet === v}
        onSelect={(v) => setDirFacet(v as DirFacet)}
      />

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.import.none', 'No import runs match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => {
            const lead = leadFor(r);
            return (
              <MobileCardRow
                key={r.upp_run_id}
                lead={lead.icon}
                leadTone={lead.tone}
                title={r.definition_name}
                subtitle={
                  `${enumLabel(DIRECTION_OPTIONS, r.direction)} · ${rowsLabel(r.row_count)}` +
                  (r.error_count > 0 ? ' · ' + errorsLabel(r.error_count) : '')
                }
                trailing={<MobileStatusPill tone={statusTone(r.status)}>{enumLabel(STATUS_OPTIONS, r.status)}</MobileStatusPill>}
                chevron
                onClick={() => setSelected(r)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.import.detail', 'Run detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{statusIcon(selected.status)}</span>
              <span>
                <div className="m-nm">{selected.definition_name}</div>
                <div className="m-em">{enumLabel(DIRECTION_OPTIONS, selected.direction)} · {enumLabel(FORMAT_OPTIONS, selected.format)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.import.status_label', 'Status')}
                value={<MobileStatusPill tone={statusTone(selected.status)}>{enumLabel(STATUS_OPTIONS, selected.status)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.import.entity', 'Entity')} value={selected.entity_name || t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.import.rows', 'Rows processed')} value={selected.row_count.toLocaleString()} />
              <MobileDetailRow label={t('mobile.import.errors', 'Errors')} value={selected.error_count.toLocaleString()} />
              <MobileDetailRow label={t('mobile.import.ran_by', 'Run by')} value={selected.ran_by_name} />
              <MobileDetailRow label={t('mobile.import.ran_at', 'Run at')} value={formatDateTime(selected.created_at)} />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
                {t('mobile.close', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
